// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { NavigationAction } from '@/components/navigation/navigation-action';
import { NavigationItem } from '@/components/navigation/navigation-item';
import { NavigationServerDiscovery } from '@/components/navigation/navigation-server-discovery';
import { UpgradeBoost } from '@/components/navigation/navigation-upgrade-boost';
import { NavigationCyberKoalaCalls } from './navigation-cyber-koala-calls.tsx';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { Server } from '@/models/server';
import { cn } from '@/lib/utils';
import { isPardusBrowser } from '@/components/check-user-agent';
import { GetApp } from './navigation-get-app';
import { useNavigate } from 'react-router-dom';
import type { UserSettings } from '@/models/user-settings';
import { API_BASE } from '@/config';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import type { WSEvent } from '@/ws/types.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { useStatus } from '@/context/status-context/use-status';
import { UserButton } from '@/components/user-button.tsx';
import { MicIndicator } from '@/context/global-audio-context/mic-indicator.tsx';
import { fetchCurrentGame } from '@/components/server/server-member.tsx';
import { formatGameName } from '@/components/gaming/get-game-info';
import { GAME_POLLING_INTERVAL } from '@/config';
import { SettingsIcon } from 'lucide-react';
import PingIndicator from '@/components/ping-indicator.tsx';
import * as React from 'react';
import { useCall } from '@/context/call-context/use-call.ts';

type Folder = {
  id: string;
  name: string;
  servers: Server[];
};

export const NavigationSidebar = () => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [orderedServers, setOrderedServers] = useState<Server[]>([]);
  const navigate = useNavigate();
  const { user: sessionUser } = useAuth();
  const socket = useSocket();
  const { status } = useStatus();
  const { activeCall, docked } = useCall();

  // Game status polling (same logic as in ServerMember)
  // TODO: use Game ActivityCard logic intead (from DB)
  const [playingGameProcessName, setPlayingGameProcessName] = useState<string | null>(null);
  const [playingGameDisplayName, setPlayingGameDisplayName] = useState<string | null>(null);
  const [gameIconUrl, setGameIconUrl] = useState<string | null>(null);

  // TODO: handle if kicked from server (from db), dont show here removed server

  const fetchData = async () => {
    try {
      const [serversRes, userSettingsRes] = await Promise.all([
        fetch(`${API_BASE}/api/servers`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_BASE}/api/user-settings`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (!serversRes.ok || !userSettingsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [servers, userSettings] = await Promise.all([
        serversRes.json() as Promise<Server[]>,
        userSettingsRes.json() as Promise<UserSettings>,
      ]);

      if (userSettings?.serverNavOrder) {
        const folders = userSettings.serverNavOrder
          .filter((item) => item.startsWith('folder_'))
          .map((folderId) => {
            const serversInFolder = folderId.split(':').slice(1);
            return {
              id: folderId,
              name: `Folder ${folderId}`,
              servers: servers.filter((server) =>
                serversInFolder.includes(server.id)
              ),
            };
          });

        setFolders(folders);

        const ordered = servers.sort((a, b) => {
          const indexA = userSettings.serverNavOrder.indexOf(a.id);
          const indexB = userSettings.serverNavOrder.indexOf(b.id);
          return indexA - indexB;
        });
        setOrderedServers(ordered);
      } else {
        setOrderedServers(servers);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (!socket) return;
    const handleMessage = (event: MessageEvent) => {
      const message: WSEvent = JSON.parse(event.data);

      switch (message.type) {
        case 'server-add': {
          const addedServer: Server = message.payload;
          const isMember = addedServer.members.some(
            (member) => member.profile.id === sessionUser?.id
          );
          if (isMember) {
            fetchData(); // сервер добавлен, обновляем список
            setOrderedServers((prev) => [...prev, addedServer]);
          }
          break;
        }
        case 'server-edit': {
          fetchData();

          const editedServer: Server = message.payload;
          const isMember = editedServer.members.some(
            (member) => member.profile.id === sessionUser?.id
          );

          if (isMember) {
            setOrderedServers((prev) =>
              prev.map((server) =>
                server.id === editedServer.id ? editedServer : server
              )
            );
          }
          break;
        }
        case 'server-leave':
          fetchData();
          const removedServerId: string = message.payload.id;
          const userId: string = message.payload.userId;
          if (userId == sessionUser?.id) {
            navigate('/discovery');
          }
          setOrderedServers((prev) =>
            prev.filter((server) => server.id !== removedServerId)
          );

          setFolders((prevFolders) =>
            prevFolders
              .map((folder) => ({
                ...folder,
                servers: folder.servers.filter(
                  (server) => server.id !== removedServerId
                ),
              }))
              .filter((folder) => folder.servers.length > 0)
          );
          break;
        case 'server-delete': {
          fetchData();
          const removedServerId: string = message.payload.id;
          // const userId: string = message.payload.userId;

          setOrderedServers((prev) =>
            prev.filter((server) => server.id !== removedServerId)
          );

          setFolders((prevFolders) =>
            prevFolders
              .map((folder) => ({
                ...folder,
                servers: folder.servers.filter(
                  (server) => server.id !== removedServerId
                ),
              }))
              .filter((folder) => folder.servers.length > 0)
          );
          break;
        }
        // Cannot access 'updatedServer' before initialization
        // ReferenceError: Cannot access 'updatedServer' before initialization
        //     at http://localhost:5173/src/components/navigation/navigation-sidebar.tsx?t=1761601704440:180:61
        //     at Array.filter (<anonymous>)
        //     at http://localhost:5173/src/components/navigation/navigation-sidebar.tsx?t=1761601704440:180:28
        //     at basicStateReducer (http://localhost:5173/node_modules/.vite/deps/react-dom_client.js?v=de3c0a18:4406:47)
        //     at updateReducerImpl (http://localhost:5173/node_modules/.vite/deps/react-dom_client.js?v=de3c0a18:4493:54)
        //     at updateReducer (http://localhost:5173/node_modules/.vite/deps/react-dom_client.js?v=de3c0a18:4439:16)
        //     at Object.useState (http://localhost:5173/node_modules/.vite/deps/react-dom_client.js?v=de3c0a18:16790:20)
        //     at exports.useState (http://localhost:5173/node_modules/.vite/deps/chunk-5LFKFUIN.js?v=de3c0a18:927:36)
        //     at NavigationSidebar (http://localhost:5173/src/components/navigation/navigation-sidebar.tsx?t=1761601704440:53:47)
        //     at react-stack-bottom-frame (http://localhost:5173/node_modules/.vite/deps/react-
        case 'member-join':
          const updatedServer: Server = message.payload.server;
          const joinedUserId: string = message.payload.userId;

          const joined = updatedServer.members.some(
            (m) => m.profileId === sessionUser?.id
          );
          const isCurrentUserJoined = joinedUserId === sessionUser?.id;

          if (joined) {
            // сервер новый для пользователя
            if (!isCurrentUserJoined) return;
            setOrderedServers((prev) => {
              const exists = prev.some((s) => s.id === updatedServer.id);
              if (!exists) {
                return [...prev, updatedServer];
              }
              return prev.map((s) =>
                s.id === updatedServer.id ? updatedServer : s
              );
            });

            fetchData();
          }
          break;
        case 'member-leave': {
          const leftServer: Server = message.payload.server;
          const leftUserId: string = message.payload.userId;
          const leftServerId = leftServer.id;
          const isCurrentUserLeft = leftUserId === sessionUser?.id;

          if (!isCurrentUserLeft) return;

          // if current user
          setOrderedServers((prev) =>
            prev.filter((server) => server.id !== leftServerId)
          );

          setFolders((prevFolders) =>
            prevFolders
              .map((folder) => ({
                ...folder,
                servers: folder.servers.filter(
                  (server) => server.id !== leftServerId
                ),
              }))
              .filter((folder) => folder.servers.length > 0)
          );

          fetchData();

          navigate('/', { replace: true });

          break;
        }
        default:
          break;
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, sessionUser]);

  useEffect(() => {
    fetchData();
  }, [sessionUser]);

  useEffect(() => {
    let isMounted = true;

    const pollCurrentGame = async () => {
      try {
        if (!sessionUser?.id) return;
        const result = await fetchCurrentGame(sessionUser.id);
        if (!isMounted) return;

        if (result) {
          const displayName = result.gameName || formatGameName(result.currentGame) || null;
          const processName = result.processName || result.currentGame || null;

          setPlayingGameDisplayName((prev) => (prev !== displayName ? displayName : prev));
          setPlayingGameProcessName((prev) => (prev !== processName ? processName : prev));
          setGameIconUrl((prev) => (prev !== result.iconUrl ? result.iconUrl : prev));
        } else {
          setPlayingGameDisplayName(null);
          setPlayingGameProcessName(null);
          setGameIconUrl(null);
        }
      } catch (e) {
        console.error('Failed to poll current game:', e);
      }
    };

    pollCurrentGame();
    const intervalId = setInterval(pollCurrentGame, GAME_POLLING_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [sessionUser?.id]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5, // было 0.1
      delay: 100, // удержание 100ms перед стартом drag
      // tolerance: 5, // допустимое движение мыши/пальца
    },
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor,
    pointerSensor
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      try {
        const activeServer = orderedServers.find(
          (server) => server.id === active.id
        );
        const overItem = orderedServers.find((server) => server.id === over.id);

        if (!activeServer || !overItem) {
          return;
        }

        const isOverFolder = over.id.toString().startsWith('folder_');

        const userSettingsRes = await fetch(`${API_BASE}/api/user-settings`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await userSettingsRes.json();

        const currentServerNavOrder = data.serverNavOrder;
        const updatedServerNavOrder = currentServerNavOrder.filter(
          (item) => !item.includes(active.id.toString())
        );

        if (isOverFolder) {
          updatedServerNavOrder.forEach((item, index) => {
            if (item === over.id) {
              updatedServerNavOrder[index] = `${item}:${active.id}`;
            }
          });
        } else {
          const overIndex = currentServerNavOrder.findIndex(
            (item) => item === over.id
          );
          updatedServerNavOrder.splice(overIndex, 0, active.id.toString());
        }

        await fetch(`${API_BASE}/api/user-settings/server-nav-order`, {
          method: 'POST',
          body: JSON.stringify({
            userId: sessionUser?.id,
            serverNavOrder: updatedServerNavOrder,
          }),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const newOrderedServers = arrayMove(
          orderedServers,
          orderedServers.findIndex((server) => server.id === active.id),
          orderedServers.findIndex((server) => server.id === over.id)
        );
        setOrderedServers(newOrderedServers);
      } catch (error) {}
    }
  };

  const handleNavigationItemClick = (id: string) => {
    navigate(`/servers/${id}`);
  };

  return (
    <div
      className={cn(
        'space-y-4 flex flex-col items-center h-full text-primary w-full dark:bg-[#1E1F22] bg-[#E3E5E8] py-3',
        isPardusBrowser() && 'pt-[2.5em]'
      )}
    >
      <NavigationCyberKoalaCalls />
      <NavigationServerDiscovery />
      <Separator className="mx-auto h-[2px] w-10 rounded-md bg-zinc-300 dark:bg-zinc-700" />
      <ScrollArea className="w-full flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={orderedServers}>
            {folders.map((folder) => (
              <div key={folder.id} className="mb-4">
                <div className="px-4 text-sm font-semibold">{folder.name}</div>
                {folder.servers.map((server) => (
                  <NavigationItem
                    key={server.id}
                    id={server.id}
                    name={server.name}
                    imageUrl={server.imageUrl || undefined}
                    onClick={handleNavigationItemClick}
                  />
                ))}
              </div>
            ))}
            {orderedServers
              .filter(
                (server) =>
                  !folders.some((folder) => folder.servers.some((s) => s.id === server.id))
              )
              .map((server) => (
                <div key={server.id} className={cn(`mb-4 z-30 `)}>
                  <NavigationItem
                    id={server.id}
                    name={server.name}
                    imageUrl={server.imageUrl || undefined}
                    onClick={handleNavigationItemClick}
                  />
                </div>
              ))}
          </SortableContext>
        </DndContext>
        <Separator className="mx-auto h-[2px] w-10 rounded-md bg-zinc-300 dark:bg-zinc-700" />
        <div className="mt-4 flex flex-col gap-2">
          <div className="flex flex-col">
            <NavigationAction />
          </div>
          <div className="hidden lg:flex">
            <GetApp />
          </div>
          <div className="flex flex-col items-center">
            <UpgradeBoost />
          </div>
        </div>
      </ScrollArea>
      <div
        id="user-bar"
        className="z-50 mx-auto ml-2 flex-col rounded-lg  shadow-sm backdrop-blur-md bg-white dark:bg-[#292b30] md:flex md:w-72"
      >
        {activeCall && docked && (
          <div className="flex items-center gap-2 rounded-t-md  px-2 py-1">
            <div className="flex items-center gap-1">
              <div className="relative">
                <PingIndicator url={activeCall.tkitServerUrl} />
              </div>

              <div className="flex flex-col leading-none">
                <span className="text-[8px] uppercase tracking-widest text-muted-foreground">
                  Подключено
                </span>
                <span className="text-[10px] uppercase font-medium text-muted-foreground">
                  {activeCall.tkitServerName}
                </span>
              </div>
            </div>
          </div>
        )}
        <div
          key="nav-sidebar-client"
          id="calls-media-room-floating-container"
          className="items-left flex"
        ></div>
        <div className="flex items-center gap-x-2 p-2">
          <UserButton />
          <div className="flex flex-col items-start justify-start">
            <span className="text-sm font-medium">
              {sessionUser?.nickname || sessionUser?.name || ''}
            </span>
            {playingGameDisplayName ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground animate-fadeIn">
                {gameIconUrl ? (
                  <img
                    src={gameIconUrl}
                    alt={playingGameDisplayName}
                    className="h-3.5 w-auto rounded"
                  />
                ) : (
                  <div className="h-3.5 w-3.5 rounded bg-gray-600" />
                )}
                <span className="truncate max-w-[120px]">{playingGameDisplayName}</span>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">{status}</span>
            )}
          </div>
          <div className="grow"></div>
          {isPardusBrowser() && <MicIndicator />}
          <button
            type="button"
            className="ml-auto flex size-5 items-center text-muted-foreground  hover:text-foreground"
            onClick={() => navigate('/channels/@me/account')}
          >
            <SettingsIcon className="size-5 text-muted-foreground transition-transform hover:rotate-90 hover:text-foreground" />
          </button>
        </div>
      </div>
      {/*<SidebarUserBar sessionUser={sessionUser} status={status} />*/}
    </div>
  );
};
